/**
 * MegaLobby.jsx — 3-row Mega Lobby that lives immediately below the
 * Hero CTA on the landing page.  Matches the approved concept render
 * (Aug 2026): gold velvet ropes, mascot patrol on each row header,
 * pulsing live "N Playing" FOMO badge per tile.
 *
 * Row behavior (Aug 2026 update)
 * ------------------------------
 * - Native horizontal scroll (`overflow-x-auto`) + `scroll-snap-type` so
 *   mobile users can flick through titles with a thumb, while desktop
 *   users see subtle chevron arrows on hover to scroll a full page
 *   left/right at a time.
 * - `flex-nowrap` explicitly disables wrap — tiles trail off the edge
 *   until the user scrolls.
 * - Native scrollbar hidden across browsers for a high-end feel.
 *
 * Category-focused mixing (Aug 2026)
 * ----------------------------------
 * - Rows are curated by *category*, not provider.  The Slots row
 *   interleaves KA, Booming, Betsoft (top 5 each) using a variety
 *   algorithm that never places more than 2 tiles of the same provider
 *   back-to-back.
 * - When the Spinjoy sync completes, its titles can be handed in via
 *   the `spinjoyTiles` prop and they'll auto-interleave into the Slots
 *   and Fish rows via the same variety algorithm.
 */
import { useMemo, useRef } from "react";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import Mascot from "./Mascot";

/* ── Curated tile pools per category (real backend thumb URLs) ─────── */

// FEATURED row — #1 title from every provider, mixed type-agnostic.
const FEATURED_TILES = [
  { id: "ka_fire_hit2",         name: "Fire Hit 2",              thumb: "/api/ka/thumb/FireHit2",                                                   provider: "KA Gaming" },
  { id: "boom_lucky_charms",    name: "Lucky Charms Trio 10,000",thumb: "/api/game-assets/booming/lucky-charms-trio-10000.png",                    provider: "Booming" },
  { id: "bet_game_show",        name: "The Game Show",           thumb: "/games/betsoft/the-game-show-real.jpg",                                     provider: "Betsoft" },
  { id: "petersons_big_bounty", name: "Big Bounty Bandits",      thumb: "/api/game-assets/petersons/big-bounty-bandits.png",                       provider: "Redwood" },
  { id: "ka_fishing_expedition",name: "Fishing Expedition",      thumb: "/api/ka/thumb/FishingExpedition",                                          provider: "KA Gaming" },
  { id: "petersons_greedy_alice",name: "Greedy Alice 2",         thumb: "/api/game-assets/petersons/greedy-alice-2.png",                            provider: "Redwood" },
  { id: "mascot_signature",     name: "Bastet & Cats",           thumb: "/api/game-assets/mascot/icons_500x500/bastet_and_cats_500x500.png",       provider: "Mascot" },
];

// Slots row: TOP 5 from KA Gaming, Booming, Betsoft — real thumbs (reels only).
const KA_SLOTS = [
  { id: "FireHit2",         name: "Fire Hit 2",         thumb: "/api/ka/thumb/FireHit2",         provider: "KA Gaming" },
  { id: "SkyboundPirates",  name: "Skybound Pirates",   thumb: "/api/ka/thumb/SkyboundPirates",  provider: "KA Gaming" },
  { id: "HyakkiYoichi",     name: "Hyakki Yoichi",      thumb: "/api/ka/thumb/HyakkiYoichi",     provider: "KA Gaming" },
  { id: "RedGhostBride",    name: "Red Ghost Bride",    thumb: "/api/ka/thumb/RedGhostBride",    provider: "KA Gaming" },
  { id: "LoveExpressLock",  name: "Love Express Lock",  thumb: "/api/ka/thumb/LoveExpress",      provider: "KA Gaming" },
];
const BOOMING_SLOTS = [
  { id: "boom_lucky_charms",   name: "Lucky Charms Trio 10,000", thumb: "/api/game-assets/booming/lucky-charms-trio-10000.png",                    provider: "Booming" },
  { id: "boom_amaterasu",      name: "Book of Amaterasu",        thumb: "/api/game-assets/mascot/icons_500x500/amaterasu_book_500x500.png",         provider: "Booming" },
  { id: "boom_anksunamun_book",name: "Book of Anksunamun",       thumb: "/api/game-assets/mascot/icons_500x500/book_of_anksunamun_500x500.png",     provider: "Booming" },
  { id: "boom_bastet",         name: "Bastet & Cats",            thumb: "/api/game-assets/mascot/icons_500x500/bastet_and_cats_500x500.png",       provider: "Booming" },
  { id: "boom_universe",       name: "Across the Universe",      thumb: "/api/game-assets/mascot/icons_500x500/across_the_universe_500x500.png",    provider: "Booming" },
];
const BETSOFT_SLOTS = [
  { id: "bet_game_show",     name: "The Game Show",           thumb: "/games/betsoft/the-game-show-real.jpg",           provider: "Betsoft" },
  { id: "bet_good_girl2",    name: "Good Girl Bad Girl 2",    thumb: "/games/betsoft/good-girl-bad-girl-2-real.jpg",    provider: "Betsoft" },
  { id: "bet_gold_nugget",   name: "Gold Nugget Rush 2",      thumb: "/games/betsoft/gold-nugget-rush-2-500x500.jpg",   provider: "Betsoft" },
  { id: "bet_bonus_day",     name: "The Bonus Day",           thumb: "/games/betsoft/the-bonus-day-500x500.jpg",        provider: "Betsoft" },
  { id: "bet_governor",      name: "The Governor",            thumb: "/games/betsoft/the-governor-500x500.jpg",         provider: "Betsoft" },
  { id: "bet_million_bc",    name: "3 Million BC",            thumb: "/games/betsoft/3-million-bc-500x500.jpg",         provider: "Betsoft" },
  { id: "bet_super_stamp",   name: "Super Stampede",          thumb: "/games/betsoft/super-stampede-500x500.jpg",       provider: "Betsoft" },
];
const PETERSONS_SLOTS = [
  { id: "petersons_big_bounty",  name: "Big Bounty Bandits", thumb: "/api/game-assets/petersons/big-bounty-bandits.png", provider: "Redwood" },
  { id: "petersons_bloody_mary", name: "Bloody Mary",        thumb: "/api/game-assets/petersons/bloody-mary.png",        provider: "Redwood" },
  { id: "petersons_greedy",      name: "Greedy Alice 2",     thumb: "/api/game-assets/petersons/greedy-alice-2.png",     provider: "Redwood" },
  { id: "petersons_bear",        name: "Bear Patrol",        thumb: "/api/game-assets/petersons/bear-patrol.png",        provider: "Redwood" },
];

// NEW ARRIVALS row — freshly-launched titles across providers.
const NEW_ARRIVALS = [
  { id: "petersons_big_bounty_new",  name: "Big Bounty Bandits", thumb: "/api/game-assets/petersons/big-bounty-bandits.png",                        provider: "Redwood" },
  { id: "ka_hyakki_new",             name: "Hyakki Yoichi",      thumb: "/api/ka/thumb/HyakkiYoichi",                                                provider: "KA Gaming" },
  { id: "boom_lucky_charms_new",     name: "Lucky Charms Trio",  thumb: "/api/game-assets/booming/lucky-charms-trio-10000.png",                     provider: "Booming" },
  { id: "bet_game_show_new",         name: "The Game Show",      thumb: "/games/betsoft/the-game-show-real.jpg",                                    provider: "Betsoft" },
  { id: "ka_red_ghost_new",          name: "Red Ghost Bride",    thumb: "/api/ka/thumb/RedGhostBride",                                               provider: "KA Gaming" },
  { id: "petersons_bloody_new",      name: "Bloody Mary",        thumb: "/api/game-assets/petersons/bloody-mary.png",                                provider: "Redwood" },
  { id: "mascot_bastet_new",         name: "Bastet & Cats",      thumb: "/api/game-assets/mascot/icons_500x500/bastet_and_cats_500x500.png",       provider: "Mascot" },
  { id: "ka_skybound_new",           name: "Skybound Pirates",   thumb: "/api/ka/thumb/SkyboundPirates",                                             provider: "KA Gaming" },
];
// Slots are DELIBERATELY excluded to preserve category integrity.
// FishingGod + MysticFishing removed — KA staging returned a 44-byte transparent GIF,
// so the tile rendered as a black box.
const ACTION_TILES = [
  { id: "FishingExpedition", name: "Fishing Expedition", thumb: "/api/ka/thumb/FishingExpedition", provider: "KA Gaming" },
  { id: "KAFishHunter",      name: "KA Fish Hunter",     thumb: "/api/ka/thumb/KAFishHunter",      provider: "KA Gaming" },
  { id: "GoldenFishHunter",  name: "Golden Fish Hunter", thumb: "/api/ka/thumb/GoldenFishHunter",  provider: "KA Gaming" },
  { id: "KAFishParty",       name: "KA Fish Party",      thumb: "/api/ka/thumb/KAFishParty",       provider: "KA Gaming" },
  { id: "OceanPrincess",     name: "Ocean Princess",     thumb: "/api/ka/thumb/OceanPrincess",     provider: "KA Gaming" },
  { id: "KingOctopus",       name: "King Octopus",       thumb: "/api/ka/thumb/KingOctopus",       provider: "KA Gaming" },
];

/**
 * Variety algorithm — never places more than 2 tiles from the same
 * provider back-to-back.  Uses a greedy scan that keeps the running
 * distribution roughly proportional to the input pools.
 */
function interleaveByProvider(pools) {
  const active = pools.filter(Boolean).map((p) => ({ list: [...p], name: p[0]?.provider || "?" }));
  const out = [];
  let sameProviderRun = 0;
  let lastProvider = null;

  while (active.some((s) => s.list.length)) {
    // Prefer the biggest remaining pool; skip it if it would create a
    // 3rd-in-a-row from the same provider.
    active.sort((a, b) => b.list.length - a.list.length);
    let picked = null;
    for (const src of active) {
      if (!src.list.length) continue;
      const same = src.name === lastProvider;
      if (same && sameProviderRun >= 2) continue;
      picked = src;
      break;
    }
    if (!picked) picked = active.find((s) => s.list.length); // safety fall-through
    const tile = picked.list.shift();
    out.push(tile);
    if (picked.name === lastProvider) sameProviderRun += 1;
    else { lastProvider = picked.name; sameProviderRun = 1; }
  }
  return out;
}

/* ── Stable "N Playing" hasher (121-247, deterministic per tile id) ── */
const playingCountFor = (id) => {
  const key = String(id || "seed");
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) & 0x7fffffff;
  return 121 + (h % 127);
};

/* ── One row (Slots / Fish / Shooting) ──────────────────────────────── */
function MegaLobbyRow({ title, subtitle, tiles, leftMascot, rightMascot, accent, testIdPrefix, onTileClick }) {
  const scrollRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // Scroll one "page" (95% of visible width) so a click reveals the next batch.
    el.scrollBy({ left: dir * (el.clientWidth * 0.95), behavior: "smooth" });
  };

  const accentCls = {
    amber:   { rope: "from-amber-400 via-yellow-300 to-amber-500",   dot: "text-amber-300",   },
    cyan:    { rope: "from-cyan-400 via-sky-300 to-cyan-500",         dot: "text-cyan-300",    },
    emerald: { rope: "from-emerald-400 via-lime-300 to-emerald-500",  dot: "text-emerald-300", },
  }[accent] || {};

  return (
    <div className="jr-mega-row group/row relative" data-testid={`${testIdPrefix}-row`}>
      {/* Row header — title + gold rope + mascot patrol on each end */}
      <div className="flex items-center gap-2 sm:gap-3 mb-4">
        <Mascot slug={leftMascot} size="sm" role="inline" tip={`${title} · protected zone`} className="!w-10 !h-10 sm:!w-12 sm:!h-12 md:!w-14 md:!h-14" />

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
            <h3 className="text-lg md:text-2xl font-black text-white tracking-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              {title}
            </h3>
            <span className={`text-[9px] sm:text-[10px] md:text-xs font-black tracking-[0.2em] uppercase ${accentCls.dot}`}>· {subtitle}</span>
          </div>
          {/* Gold Velvet Rope */}
          <div className="relative mt-2 h-2">
            <span aria-hidden className="absolute inset-y-0 left-0 w-3 rounded-full bg-gradient-to-b from-amber-200 to-amber-600 border border-amber-800/40 shadow-[0_0_10px_rgba(251,191,36,0.55)]" />
            <span aria-hidden className={`absolute inset-y-0 left-3 right-3 rounded-full bg-gradient-to-r ${accentCls.rope} opacity-90`} style={{ boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.35), inset 0 2px 0 rgba(255,255,255,0.35)" }} />
            <span aria-hidden className="absolute inset-y-0 right-0 w-3 rounded-full bg-gradient-to-b from-amber-200 to-amber-600 border border-amber-800/40 shadow-[0_0_10px_rgba(251,191,36,0.55)]" />
          </div>
        </div>

        <Mascot slug={rightMascot} size="sm" role="inline" tip={`${title} · velvet-rope VIP`} className="!w-10 !h-10 sm:!w-12 sm:!h-12 md:!w-14 md:!h-14" />
      </div>

      {/* Carousel — arrows visible on hover (desktop), touch-swipe on mobile */}
      <div className="relative">
        {/* Left arrow */}
        <button
          type="button"
          aria-label={`Scroll ${title} left`}
          onClick={() => scrollBy(-1)}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/60 border border-amber-400/40 text-amber-200 opacity-0 group-hover/row:opacity-100 hover:bg-amber-500/20 hover:border-amber-300/80 backdrop-blur-sm transition-all"
          data-testid={`${testIdPrefix}-arrow-left`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Right arrow */}
        <button
          type="button"
          aria-label={`Scroll ${title} right`}
          onClick={() => scrollBy(1)}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/60 border border-amber-400/40 text-amber-200 opacity-0 group-hover/row:opacity-100 hover:bg-amber-500/20 hover:border-amber-300/80 backdrop-blur-sm transition-all"
          data-testid={`${testIdPrefix}-arrow-right`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div
          ref={scrollRef}
          className="jr-mega-scroll flex flex-nowrap gap-3 md:gap-4 overflow-x-auto pb-3"
          role="list"
        >
          {tiles.map((t, i) => (
            <button
              key={`${t.id}-${i}`}
              type="button"
              onClick={() => onTileClick?.(t)}
              className="group relative flex-shrink-0 w-[46%] xs:w-[38%] sm:w-44 md:w-48 lg:w-52 rounded-2xl overflow-hidden border border-amber-500/25 bg-black/60 backdrop-blur-sm hover:border-amber-400/70 hover:-translate-y-0.5 transition-all snap-start cursor-pointer"
              data-testid={`${testIdPrefix}-tile-${i}`}
              role="listitem"
              title={`Play ${t.name}`}
            >
              <span
                className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/25 border border-emerald-400/60 text-emerald-200 text-[9px] font-black tracking-wider uppercase backdrop-blur-sm shadow-[0_0_10px_rgba(52,211,153,0.45)]"
                data-testid={`${testIdPrefix}-fomo-${i}`}
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                </span>
                {playingCountFor(t.id)} Playing
              </span>

              <div className="aspect-square bg-slate-900 overflow-hidden">
                <img
                  src={t.thumb}
                  alt={t.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700"
                  onError={(e) => { e.currentTarget.style.opacity = "0.35"; }}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 text-[10px] font-black shadow-[0_0_18px_rgba(251,191,36,0.6)]">
                  <Play className="w-3 h-3" /> PLAY FREE
                </span>
              </div>
              <div className="px-2.5 py-2 border-t border-white/[0.05]">
                <p className="text-[11px] md:text-xs font-bold text-white truncate" style={{ fontFamily: "'Unbounded', sans-serif" }}>{t.name}</p>
                <p className="text-[9px] text-amber-300/80 truncate">{t.provider}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <style>{`
        .jr-mega-scroll {
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .jr-mega-scroll::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}

/* ── MegaLobby (3-row wrapper with clean hierarchy Aug 2026) ────────── */
export default function MegaLobby({ slotsTiles: providedSlots = [], spinjoyTiles = [], onTileClick }) {
  // Row 3 — HOT SLOTS: reels only (no fish, no shooters), variety mixed.
  const slotsMix = useMemo(() => {
    const mixed = interleaveByProvider([KA_SLOTS, BOOMING_SLOTS, BETSOFT_SLOTS, PETERSONS_SLOTS, spinjoyTiles]);
    if (providedSlots.length) {
      const head = providedSlots.slice(0, 4);
      const tail = interleaveByProvider([KA_SLOTS, BOOMING_SLOTS, BETSOFT_SLOTS, PETERSONS_SLOTS, spinjoyTiles]);
      return [...head, ...tail].slice(0, 20);
    }
    return mixed.slice(0, 20);
  }, [providedSlots, spinjoyTiles]);

  return (
    <section
      id="mega-lobby"
      className="relative py-12 md:py-20 overflow-hidden"
      data-testid="mega-lobby"
      style={{
        background: "radial-gradient(1200px 500px at 50% 0%, rgba(255,215,0,0.06), transparent 60%), linear-gradient(180deg, #0a0605 0%, #0d0806 100%)",
      }}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.06] blur-[140px]" />
        <div className="absolute -bottom-24 right-1/4 w-[420px] h-[420px] rounded-full bg-amber-400/[0.08] blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-10">
          <span className="text-[10px] sm:text-xs font-black tracking-[0.4em] uppercase bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-transparent">
            ✦  ENTER THE VAULT  ✦
          </span>
          <h2
            className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mt-3"
            style={{ fontFamily: "'Unbounded', sans-serif", textShadow: "0 0 30px rgba(251,191,36,0.35)" }}
          >
            The <span className="bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-500 bg-clip-text text-transparent">Mega Lobby</span>
          </h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm sm:text-base">
            Every studio. Every stake. Swipe or click to explore.
          </p>
        </div>

        <div className="space-y-10">
          {/* Row 1 — FEATURED: #1 title from every provider (type-agnostic mix) */}
          <MegaLobbyRow
            title="Featured"
            subtitle="Best of the Best"
            tiles={FEATURED_TILES}
            leftMascot="king"
            rightMascot="wizard"
            accent="amber"
            testIdPrefix="mega-featured"
            onTileClick={onTileClick}
          />
          {/* Row 2 — NEW ARRIVALS: freshly-launched titles across providers */}
          <MegaLobbyRow
            title="New Arrivals"
            subtitle="Fresh Drops · This Week"
            tiles={NEW_ARRIVALS}
            leftMascot="astronaut"
            rightMascot="wizard"
            accent="emerald"
            testIdPrefix="mega-newarrivals"
            onTileClick={onTileClick}
          />
          {/* Row 3 — FISH TABLES & ACTION: interactive titles only (no slots) */}
          <MegaLobbyRow
            title="Fish Tables & Action"
            subtitle="Interactive · Shoot · Cash"
            tiles={ACTION_TILES}
            leftMascot="egyptian_princess"
            rightMascot="astronaut"
            accent="cyan"
            testIdPrefix="mega-action"
            onTileClick={onTileClick}
          />
          {/* Row 4 — HOT SLOTS: reels only, provider variety */}
          <MegaLobbyRow
            title="Hot Slots"
            subtitle="Reels · Spin to Win"
            tiles={slotsMix}
            leftMascot="viking_warrior"
            rightMascot="mafia_boss"
            accent="emerald"
            testIdPrefix="mega-slots"
            onTileClick={onTileClick}
          />
        </div>
      </div>
    </section>
  );
}
